import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import NavigationBar from '../../components/ui/NavigationBar';
import ServicesBreadcrumb from '../../components/ui/ServicesBreadcrumb';
import ServiceHero from './components/ServiceHero';
import ServiceContent from './components/ServiceContent';
import ServiceSidebar from './components/ServiceSidebar';
import TestimonialSlider from './components/TestimonialSlider';
import FloatingNavigation from '../../components/ui/FloatingNavigation';

const ServicesDetailPage = () => {
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Simulate loading
    const timer = setTimeout(() => setIsLoading(false), 1000);
    return () => clearTimeout(timer);
  }, []);

  // Mock service data
  const serviceData = {
    title: "Full-Stack Development",
    subtitle: "End-to-end web and mobile application development with modern technologies",
    tags: ["React", "Node.js", "Cloud Native", "Mobile First"],
    description: `Our full-stack development service provides comprehensive solutions for modern web and mobile applications. We use the latest technologies and best practices to build scalable, secure, and user-friendly applications that drive business growth.\n\nFrom concept to deployment, our experienced team handles every aspect of the development lifecycle, ensuring your project is delivered on time and exceeds expectations.`,
    features: [
      {
        icon: "Code",
        title: "Modern Tech Stack",
        description: "React, Node.js, Python, and cloud-native technologies"
      },
      {
        icon: "Smartphone",
        title: "Mobile-First Design",
        description: "Responsive applications that work seamlessly across all devices"
      },
      {
        icon: "Shield",
        title: "Security First",
        description: "Built-in security measures and compliance with industry standards"
      },
      {
        icon: "Zap",
        title: "Performance Optimized",
        description: "Fast loading times and optimized user experiences"
      },
      {
        icon: "Users",
        title: "Scalable Architecture",
        description: "Built to grow with your business and handle increased traffic"
      },
      {
        icon: "Headphones",
        title: "Ongoing Support",
        description: "Continuous maintenance and support after deployment"
      }
    ],
    methodology: [
      {
        title: "Discovery & Planning",
        description: "We start by understanding your business goals, target audience, and technical requirements to create a comprehensive project roadmap."
      },
      {
        title: "Design & Prototyping",
        description: "Our design team creates wireframes and interactive prototypes to visualize the user experience before development begins."
      },
      {
        title: "Development & Testing",
        description: "Using agile methodologies, we build your application with continuous testing and quality assurance throughout the process."
      },
      {
        title: "Deployment & Launch",
        description: "We handle the complete deployment process, ensuring your application is properly configured and optimized for production."
      },
      {
        title: "Monitoring & Support",
        description: "Post-launch monitoring and ongoing support to ensure optimal performance and address any issues that may arise."
      }
    ],
    technologies: [
      { name: "React", category: "Frontend", icon: "Code" },
      { name: "Node.js", category: "Backend", icon: "Server" },
      { name: "Python", category: "Backend", icon: "Code2" },
      { name: "PostgreSQL", category: "Database", icon: "Database" },
      { name: "MongoDB", category: "Database", icon: "Database" },
      { name: "AWS", category: "Cloud", icon: "Cloud" },
      { name: "Docker", category: "DevOps", icon: "Package" },
      { name: "Kubernetes", category: "DevOps", icon: "Settings" },
      { name: "GraphQL", category: "API", icon: "Share2" },
      { name: "TypeScript", category: "Language", icon: "FileCode" },
      { name: "Next.js", category: "Framework", icon: "Layers" },
      { name: "React Native", category: "Mobile", icon: "Smartphone" }
    ],
    caseStudies: [
      {
        title: "E-commerce Platform Transformation",
        client: "TechMart Solutions",
        description: "Complete redesign and development of a multi-vendor e-commerce platform serving over 100,000 users. Implemented advanced search, real-time inventory management, and integrated payment processing.",
        result: "300% increase in sales",
        technologies: ["React", "Node.js", "PostgreSQL", "AWS", "Stripe"]
      },
      {
        title: "Healthcare Management System",
        client: "MediCare Plus",
        description: "Built a comprehensive healthcare management system with patient portals, appointment scheduling, and telemedicine capabilities. HIPAA compliant with advanced security features.",
        result: "50% reduction in admin time",
        technologies: ["React", "Python", "MongoDB", "Docker", "WebRTC"]
      },
      {
        title: "Financial Analytics Dashboard",
        client: "InvestPro Analytics",
        description: "Developed a real-time financial analytics dashboard with advanced charting, portfolio management, and automated reporting features for investment professionals.",
        result: "40% faster decision making",
        technologies: ["React", "Node.js", "D3.js", "Redis", "WebSocket"]
      }
    ],
    faqs: [
      {
        question: "What technologies do you use for full-stack development?",
        answer: "We use modern technologies including React, Node.js, Python, PostgreSQL, MongoDB, and cloud platforms like AWS. Our tech stack is chosen based on your specific project requirements and scalability needs."
      },
      {
        question: "How long does a typical full-stack project take?",
        answer: "Project timelines vary based on complexity and scope. A simple web application might take 8-12 weeks, while complex enterprise solutions can take 6-12 months. We provide detailed timelines during the planning phase."
      },
      {
        question: "Do you provide ongoing maintenance and support?",
        answer: "Yes, we offer comprehensive maintenance and support packages including bug fixes, security updates, performance monitoring, and feature enhancements to keep your application running smoothly."
      },
      {
        question: "Can you work with our existing systems and APIs?",
        answer: "Absolutely! We have extensive experience integrating with existing systems, third-party APIs, and legacy applications. We ensure seamless data flow and maintain system compatibility."
      },
      {
        question: "What is your approach to security and data protection?",
        answer: "Security is built into every layer of our applications. We implement industry-standard security practices including encryption, secure authentication, regular security audits, and compliance with relevant regulations."
      }
    ],
    highlights: [
      "99.9% uptime guarantee",
      "24/7 technical support",
      "Agile development methodology",
      "Code quality assurance",
      "Performance optimization",
      "Security best practices",
      "Scalable architecture",
      "Cross-platform compatibility"
    ],
    pricingTiers: [
      {
        name: "Starter",
        price: "$15,000 - $30,000",
        description: "Perfect for small businesses and startups",
        popular: false,
        features: [
          "Basic web application",
          "Responsive design",
          "User authentication",
          "Database integration",
          "3 months support"
        ]
      },
      {
        name: "Professional",
        price: "$30,000 - $75,000",
        description: "Ideal for growing businesses",
        popular: true,
        features: [
          "Advanced web application",
          "Mobile app included",
          "Third-party integrations",
          "Admin dashboard",
          "6 months support",
          "Performance optimization"
        ]
      },
      {
        name: "Enterprise",
        price: "$75,000+",
        description: "For large-scale applications",
        popular: false,
        features: [
          "Complex enterprise solution",
          "Multiple platforms",
          "Custom integrations",
          "Advanced analytics",
          "12 months support",
          "Dedicated team",
          "Priority support"
        ]
      }
    ]
  };

  // Mock related services
  const relatedServices = [
    {
      name: "UI/UX Design",
      description: "User-centered design solutions",
      icon: "Palette"
    },
    {
      name: "Cloud Services",
      description: "Scalable cloud infrastructure",
      icon: "Cloud"
    },
    {
      name: "Mobile Development",
      description: "Native and hybrid mobile apps",
      icon: "Smartphone"
    },
    {
      name: "DevOps Solutions",
      description: "Automated deployment pipelines",
      icon: "Settings"
    }
  ];

  // Mock testimonials
  const testimonials = [
    {
      content: "DARAJA.LABS transformed our outdated system into a modern, efficient platform. Their full-stack expertise and attention to detail exceeded our expectations. The team was professional, responsive, and delivered on time.",
      name: "Sarah Johnson",
      position: "CTO",
      company: "TechMart Solutions",
      avatar: "public/assets/images/cyrus.png",
      rating: 5
    },
    {
      content: "Working with DARAJA.LABS was a game-changer for our healthcare platform. They understood our complex requirements and delivered a HIPAA-compliant solution that our patients and staff love using.",
      name: "Dr. Michael Chen",
      position: "Medical Director",
      company: "MediCare Plus",
      avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face",
      rating: 5
    },
    {
      content: "The financial analytics dashboard they built for us has revolutionized how we analyze market data. Real-time insights and beautiful visualizations have made our decision-making process much more efficient.",
      name: "Jennifer Rodriguez",
      position: "Portfolio Manager",
      company: "InvestPro Analytics",
      avatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face",
      rating: 5
    }
  ];

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          className="w-8 h-8 border-2 border-accent border-t-transparent rounded-full"
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <NavigationBar />
      <FloatingNavigation />
      {/* Breadcrumb Navigation */}
      <div className="pt-20">
        <ServicesBreadcrumb 
          serviceCategory="Services" 
          serviceName="Full-Stack Development" 
        />
      </div>
      {/* Service Hero Section */}
      <section id="hero">
        <ServiceHero service={serviceData} />
      </section>
      {/* Main Content Area */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid lg:grid-cols-12 gap-8">
          {/* Main Content - 8 columns */}
          <div className="lg:col-span-8">
            <div id="overview">
              <ServiceContent service={serviceData} />
            </div>
          </div>

          {/* Sidebar - 4 columns */}
          <div className="lg:col-span-4">
            <div className="sticky top-24">
              <ServiceSidebar 
                service={serviceData} 
                relatedServices={relatedServices} 
              />
            </div>
          </div>
        </div>
      </div>
      {/* Testimonials Section */}
      <section id="testimonials">
        <TestimonialSlider testimonials={testimonials} />
      </section>
      {/* Footer CTA Section */}
      <section id="cta" className="py-16 bg-gradient-to-r from-accent/10 to-accent/5 border-t border-border">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              Ready to Start Your Project?
            </h2>
            <p className="text-text-secondary mb-8 max-w-2xl mx-auto">
              Let's discuss how our full-stack development services can help transform your business. 
              Get in touch with our experts today for a free consultation.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.a
                href="/application"
                className="inline-flex items-center justify-center px-8 py-3 bg-accent text-accent-foreground font-medium rounded-lg hover:bg-accent/90 transition-colors duration-200"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Get Started Now
              </motion.a>
              <motion.a
                href="/contact"
                className="inline-flex items-center justify-center px-8 py-3 border border-accent text-accent font-medium rounded-lg hover:bg-accent hover:text-accent-foreground transition-colors duration-200"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Schedule Consultation
              </motion.a>
            </div>
          </motion.div>
        </div>
      </section>
      {/* Footer */}
      <footer className="bg-card border-t border-border py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="flex items-center justify-center space-x-2 mb-4">
              <div className="w-8 h-8 bg-accent rounded-lg flex items-center justify-center">
                <span className="text-accent-foreground font-bold text-lg">D</span>
              </div>
              <span className="text-foreground font-semibold text-xl">DARAJA.LABS</span>
            </div>
            <p className="text-text-secondary mb-4">
              Bridging the gap between technology and business success
            </p>
            <p className="text-sm text-muted-foreground">
              © {new Date()?.getFullYear()} DARAJA.LABS. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ServicesDetailPage;